import React from 'react';
import DataVisualisationCellContents from '@components/table/sub-components/data-visualisation-cell-contents';
import DynamicFilterBlock from '@components/dynamic-filter-block/dynamic-filter-block';
import ListingTableKey from '@components/global-commitment/listing-table-key/listing-table-key';
import NoResultsFound from '@components/global-commitment/no-results-found/no-results-found';
import OrganisationNameCell from '@components/table/sub-components/organisation-name-cell';
import styled from '@emotion/styled/macro';
import Table from '@components/table/table';
import TableCell from '@components/table/sub-components/table-cell';
import TableRow from '@components/table/sub-components/table-row';
import useFilter from '@hooks/use-filter';
import vars from '@variables';
import {
  convertStringToBoolean,
  convertStringToFloat,
  generateKey,
  formatNumberWithCommas,
} from '@utils/utils';
import { generateGCReportUrl } from '../../utils/generate-url-functions';
import { ListingtableProps } from './global-commitment-table.types';
import { ppuData as tableData } from '../../data/global-commitment-report-2021/listing-page.data';
import { useStaticQuery, graphql } from 'gatsby';

const PpuListingTable: React.FC<ListingtableProps> = ({ className }) => {
  const data = useStaticQuery(graphql`
    query AllPpuQuery {
      ppu: allGcPpuDataCsv {
        nodes {
          id
          category: category__b_0
          slug: slug__b_4
          name: organisation_name__b_1
          sortableName: organisation_name_sortable__b_1_bis
          sector: sector__b_2
          publishReport: publish_report__b_3
          isFirstYear: first_year__rep
          isDeviated: deviation__dev
          plasticPackagingWeight: weight_new_plastic_packaging__metrics_tonnes___4_1_1
          plasticPackagingWeightAltText: weight_new_plastic_packaging__alternative__4_2
          reusablePlasticPackaging: _2020_reusable_plastic_packaging__percent___4_5_13
          sortableReusablePlasticPackaging: reusable_packaging__sorting__4_5_sort
          previousReusablePlasticPackaging: _2019_reusable_plastic_packaging__percent___3_2_12
          reusablePlasticPackagingAltText: reusable_packaging__alternative__4_5_13_excep
          rrcPlasticPackaging: _2020_reusable_recyclable_or_compostable__percent___4_5_1
          sortableRrcPlasticPackaging: reusable_recyclable_or_compostable__sorting__4_5_sort
          previousRrcPlasticPackaging: _2019_reusable_recyclable_or_compostable__percent___3_2_17
          rrcPlasticPackagingAltText: _2020_reusable_recyclable_or_compostable__alternative__4_5_1_excep
          rrcTarget: target_reusable_recyclable_and_or_compostable__rrc_target
          rrcTargetAltText: target_reusable_recyclable_and_or_compostable__alternative__rrc_target_excep
          pcrContent: _2020_postconsumer_recycled_content__percent___4_4_11
          sortablePcrContent: postconsumer_recycled_content__sorting__4_4_sort
          previousPcrContent: _2019_postconsumer_recycled_content__percent___3_2_5
          pcrContentAltText: postconsumer_recycled_content__alternative__4_4_11_excep
          pcrTarget: target_postconsumer_recycled_content__percent___pcr_target
          sortablePcrTarget: target_postconsumer_recycled_content__sorting__pcr_target_sort
          pcrTargetAltText: target_postconsumer_recycled_content__alternative__pcr_target_excep
          reductionTargetRankVirgin: reduction_target__rank_on_virgin_targets__rank_virgin
          reductionTargetRankTotal: reduction_target__rank_on_total_targets__rank_total
          reductionTargetText: reduction_target__text__rt_text_short
          reductionTargetType: reduction_target__type__rt_type
        }
      }
    }
  `);

  const allOrganisationData = data.ppu.nodes;
  const { dynamicFilterBlockData, tableHeaderData } = tableData;
  const {
    filteredData,
    updateQueryArray,
    setCompareFunctionConfig,
  } = useFilter(allOrganisationData);

  return (
    <div className={className}>
      <DynamicFilterBlock
        updateQueryArray={updateQueryArray}
        setCompareFunctionConfig={setCompareFunctionConfig}
        elementData={dynamicFilterBlockData}
      />
      <ListingTableKey />
      {filteredData.length > 0 ? (
        <div className="table-container">
          <Table tableHeaderData={tableHeaderData}>
            {filteredData.map((organisation, index) => {
              return (
                <TableRow
                  key={generateKey(organisation.name, index)}
                  url={
                    convertStringToBoolean(organisation.publishReport)
                      ? generateGCReportUrl(
                          organisation.category,
                          organisation.name,
                          organisation.slug
                        )
                      : null
                  }
                >
                  <OrganisationNameCell
                    name={organisation.name}
                    sector={organisation.sector}
                    tag={
                      convertStringToBoolean(organisation.isFirstYear)
                        ? {
                            text: 'first year',
                            colour: vars.plasticsBlue,
                          }
                        : null
                    }
                  />
                  <TableCell>
                    {organisation.plasticPackagingWeight
                      ? formatNumberWithCommas(
                          organisation.plasticPackagingWeight
                        )
                      : organisation.plasticPackagingWeightAltText}
                  </TableCell>
                  <TableCell>
                    {organisation.reusablePlasticPackaging !== '' ? (
                      <DataVisualisationCellContents
                        currentValue={convertStringToFloat(
                          organisation.reusablePlasticPackaging
                        )}
                        previousValue={
                          organisation.previousReusablePlasticPackaging
                            ? convertStringToFloat(
                                organisation.previousReusablePlasticPackaging
                              )
                            : undefined
                        }
                        isFirstYear={convertStringToBoolean(
                          organisation.isFirstYear
                        )}
                      />
                    ) : (
                      organisation.reusablePlasticPackagingAltText
                    )}
                  </TableCell>
                  <TableCell>
                    {organisation.rrcPlasticPackaging !== '' ? (
                      <DataVisualisationCellContents
                        currentValue={convertStringToFloat(
                          organisation.rrcPlasticPackaging
                        )}
                        previousValue={
                          organisation.previousRrcPlasticPackaging
                            ? convertStringToFloat(
                                organisation.previousRrcPlasticPackaging
                              )
                            : undefined
                        }
                        target={convertStringToFloat(organisation.rrcTarget)}
                        displayTarget={
                          organisation.rrcTarget
                            ? `Target: ${organisation.rrcTarget}%`
                            : organisation.rrcTargetAltText
                        }
                        isFirstYear={convertStringToBoolean(
                          organisation.isFirstYear
                        )}
                        isDeviated={convertStringToBoolean(
                          organisation.isDeviated
                        )}
                        displayGraph={true}
                      />
                    ) : (
                      organisation.rrcPlasticPackagingAltText
                    )}
                  </TableCell>
                  <TableCell>
                    {organisation.pcrContent !== '' ? (
                      <DataVisualisationCellContents
                        currentValue={convertStringToFloat(
                          organisation.pcrContent
                        )}
                        previousValue={
                          organisation.previousPcrContent
                            ? convertStringToFloat(
                                organisation.previousPcrContent
                              )
                            : undefined
                        }
                        target={convertStringToFloat(organisation.pcrTarget)}
                        displayTarget={
                          organisation.pcrTarget
                            ? `Target: ${organisation.pcrTarget}%`
                            : organisation.pcrTargetAltText
                        }
                        isFirstYear={convertStringToBoolean(
                          organisation.isFirstYear
                        )}
                        displayGraph={true}
                      />
                    ) : (
                      organisation.pcrContentAltText
                    )}
                  </TableCell>
                  <TableCell>{organisation.reductionTargetText}</TableCell>
                </TableRow>
              );
            })}
          </Table>
        </div>
      ) : (
        <NoResultsFound />
      )}
    </div>
  );
};

const StyledPpuListingTable = styled(PpuListingTable)`
  .table-container {
    overflow-x: auto;
    ${Table} {
      width: ${vars.contentMaxWidth};
    }
  }
`;

export default StyledPpuListingTable;
